import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@quotech/auth'
import { ncrApi } from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import {
  Plus,
  Search,
  Filter,
  ChevronDown,
  AlertTriangle,
  Clock,
  CheckCircle,
  X,
  RefreshCw,
  Building2,
  Calendar,
  ArrowUpRight,
} from 'lucide-react'

type NCRSeverity = 'low' | 'medium' | 'high' | 'critical'
type NCRStatus = 'open' | 'in_progress' | 'ready_for_review' | 'closed'

const severityOptions = [
  { value: '', label: 'All Severity' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready_for_review', label: 'Ready for Review' },
  { value: 'closed', label: 'Closed' },
]

export default function NCRsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  // Wait for org context before fetching - prevents errors during auth init
  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-ncrs', { severity: severityFilter, status: statusFilter, page, organisationId }],
    queryFn: () => ncrApi.getNCRs({ 
      severity: severityFilter || undefined,
      status: statusFilter || undefined, 
      page, 
      page_size: 20 
    }),
    enabled: isAuthReady,
  })
  
  // Show loading when auth is initializing
  const showLoading = isLoading || !isAuthReady

  const ncrs = Array.isArray(data?.results) ? data.results : []
  const filteredNCRs = ncrs.filter((ncr: Record<string, unknown>) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      ncr.title?.toLowerCase().includes(query) ||
      (ncr.issue_description || ncr.description)?.toLowerCase().includes(query)
    )
  })

  const getSeverityStyles = (severity: NCRSeverity) => {
    const styles = {
      low: { 
        bg: 'bg-amber-100 dark:bg-amber-900/30', 
        text: 'text-amber-700 dark:text-amber-400',
        icon: 'text-amber-500',
        border: 'border-amber-200 dark:border-amber-800'
      },
      medium: { 
        bg: 'bg-orange-100 dark:bg-orange-900/30', 
        text: 'text-orange-700 dark:text-orange-400',
        icon: 'text-orange-500',
        border: 'border-orange-200 dark:border-orange-800'
      },
      high: {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-400',
        icon: 'text-red-500',
        border: 'border-red-200 dark:border-red-800',
      },
      critical: { 
        bg: 'bg-red-100 dark:bg-red-900/30', 
        text: 'text-red-700 dark:text-red-400',
        icon: 'text-red-500',
        border: 'border-red-200 dark:border-red-800'
      },
    }
    return styles[severity] || styles.low
  }

  const getStatusStyles = (status: NCRStatus) => {
    const styles = {
      open: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
      in_progress: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
      ready_for_review: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
      closed: { bg: 'bg-muted', text: 'text-muted-foreground' },
    }
    return styles[status] || styles.open
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Non-Conformance Records</h1>
          <p className="text-muted-foreground">Track and manage quality issues and defects</p>
        </div>
        <Link 
          to="/ncrs/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 dark:hover:bg-primary-400 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
        >
          <Plus className="w-4 h-4" />
          Raise NCR
        </Link>
      </div>

      {/* Summary */}
      {typeof data?.count === 'number' && (
        <div className="text-sm text-muted-foreground">
          Total NCRs: <span className="font-semibold text-foreground">{data.count}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search NCRs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value)
                setPage(1)
              }}
              className="appearance-none pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent cursor-pointer min-w-[140px]"
            >
              {severityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="appearance-none pl-4 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent cursor-pointer min-w-[160px]"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>

          <button
            onClick={() => refetch()}
            className="p-2.5 rounded-xl border border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* NCRs list */}
      <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
        {showLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <p className="font-medium text-foreground">Error loading NCRs</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
          </div>
        ) : filteredNCRs?.length ? (
          <div className="divide-y divide-border">
            {filteredNCRs.map((ncr: Record<string, unknown>) => {
              const severityStyles = getSeverityStyles(ncr.severity)
              const statusStyles = getStatusStyles(ncr.status)
              return (
                <Link
                  key={ncr.id}
                  to={`/ncrs/${ncr.id}`}
                  className={cn(
                    'flex items-start justify-between p-4 hover:bg-accent/50 transition-colors group border-l-4',
                    severityStyles.border
                  )}
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5', severityStyles.bg)}>
                      <AlertTriangle className={cn('w-5 h-5', severityStyles.icon)} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded uppercase', severityStyles.bg, severityStyles.text)}>
                          {ncr.severity}
                        </span>
                        <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', statusStyles.bg, statusStyles.text)}>
                          {ncr.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {ncr.title}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                        {ncr.issue_description || ncr.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {ncr.job_id ? `Job #${ncr.job_id}` : (ncr.job ? `Job #${ncr.job}` : 'No job')}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(ncr.raised_at)}
                        </span>
                        {ncr.due_date && (
                          <span className={cn(
                            "text-xs flex items-center gap-1",
                            new Date(ncr.due_date) < new Date() ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'
                          )}>
                            <Clock className="w-3 h-3" />
                            Due: {formatDate(ncr.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="font-medium text-foreground">No NCRs found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchQuery || severityFilter || statusFilter
                ? 'Try adjusting your search or filters'
                : 'No non-conformance records have been raised'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.count > 20 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{((page - 1) * 20) + 1}</span> - <span className="font-medium text-foreground">{Math.min(page * 20, data.count)}</span> of <span className="font-medium text-foreground">{data.count}</span> NCRs
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * 20 >= data.count}
              className="px-4 py-2 rounded-lg border border-input text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

